import { ArrowUpRight } from "lucide-react";
import { Chapter, MaskedLines, Fade } from "./motion";

const CONTACT = "vigneshkumarsekar@qrus.ai";

const ITEMS = [
    {
        id: "I.01",
        title: "Employability Infrastructure",
        desc: "QRUS installs inside the campus as a system, not a vendor — measured against employability outcomes, year over year.",
    },
    {
        id: "I.02",
        title: "Industry Exposure Pipeline",
        desc: "Structured exposure to industry: internships, live projects, and professional standards — built into the academic calendar.",
    },
    {
        id: "I.03",
        title: "Student Development Telemetry",
        desc: "Every student carries a live capability map. Leadership sees progression as data, not anecdote.",
    },
    {
        id: "I.04",
        title: "IP & Innovation Support",
        desc: "Research mentoring, paper development, and patent evaluation that convert student work into institutional innovation.",
    },
    {
        id: "I.05",
        title: "Career Preparation",
        desc: "Interview readiness, portfolios, and placement simulations — engineered long before placement season begins.",
    },
    {
        id: "I.06",
        title: "Long-Term Progression",
        desc: "A four-year arc, tracked at every stage. Development compounds instead of resetting each semester.",
    },
];

export default function Institutions() {
    return (
        <section id="institutions" className="bg-white py-20 md:py-40" data-testid="institutions-section">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16">
                <Chapter index="CH.06" title="For Institutions" />

                <div className="mt-16 grid gap-16 md:mt-24 lg:grid-cols-12 lg:gap-10">
                    <div className="lg:col-span-5">
                        <div className="lg:sticky lg:top-28">
                            <MaskedLines
                                stagger={0.13}
                                lineClassName="font-display text-[clamp(2.1rem,7vw,4.6rem)] font-semibold leading-[1.12] tracking-[-0.02em] text-ink"
                                lines={[
                                    <span key="a">Employability,</span>,
                                    <span key="b">installed as</span>,
                                    <span key="c" className="text-cobalt">infrastructure.</span>,
                                ]}
                            />
                            <Fade delay={0.35} className="mt-10 max-w-md">
                                <p className="text-base leading-relaxed text-slate md:text-lg">
                                    QRUS works with university and college leadership to embed the
                                    system inside the campus ecosystem — without disrupting academic
                                    structure.
                                </p>
                                <a
                                    data-testid="institutions-cta"
                                    href={`mailto:${CONTACT}?subject=Institutional%20Partnership%20—%20QRUS`}
                                    className="group mt-10 inline-flex items-center gap-2.5 border border-ink/20 px-7 py-4 text-sm font-medium tracking-wide text-ink transition-colors duration-300 hover:border-cobalt hover:text-cobalt"
                                >
                                    Start a conversation
                                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </a>
                            </Fade>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        {ITEMS.map((item, i) => (
                            <Fade key={item.id} delay={0.08 * i} y={20}>
                                <div
                                    data-testid={`institution-item-${i + 1}`}
                                    className="group flex gap-6 border-t border-ink/10 py-8 transition-colors duration-300 hover:bg-paper md:gap-10 md:py-10 lg:pl-6"
                                >
                                    <span className="pt-1 font-display text-xs font-semibold tracking-[0.25em] text-slate transition-colors duration-300 group-hover:text-cobalt">
                                        {item.id}
                                    </span>
                                    <div>
                                        <h3 className="font-display text-xl font-semibold tracking-tight text-ink transition-colors duration-300 group-hover:text-cobalt md:text-2xl">
                                            {item.title}
                                        </h3>
                                        <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate md:text-base">
                                            {item.desc}
                                        </p>
                                    </div>
                                </div>
                            </Fade>
                        ))}
                        <div className="border-t border-ink/10" />
                    </div>
                </div>
            </div>
        </section>
    );
}
